import { SignatureKeyNotProvidedError } from '../errors/signature-key-not-provided-error';
import { KeyService } from './key-service';
import { SavedKeyService } from './saved-key-service';
import { ConfirmModalCancelledError } from '../errors/confirm-modal-cancelled-error';
import { GotoMyPc } from '../models/products';
import { MonotonicTimeSource } from './time-service';
export class UserKeyPromptService {
    constructor(uiFrameAdapter, userKeysRepository, keyVaultService, companyKeyService, cryptoService, vaultRequestRepository, logger, localStorage) {
        this.uiFrameAdapter = uiFrameAdapter;
        this.userKeysRepository = userKeysRepository;
        this.keyVaultService = keyVaultService;
        this.companyKeyService = companyKeyService;
        this.cryptoService = cryptoService;
        this.vaultRequestRepository = vaultRequestRepository;
        this.logger = logger;
        this.localStorage = localStorage;
        this.maxRetryCount = 5;
        this.savedKeyService = new SavedKeyService(uiFrameAdapter, userKeysRepository, cryptoService, logger, localStorage);
        this.keyService = new KeyService(uiFrameAdapter, userKeysRepository, cryptoService, logger);
    }
    /** Load key or prompt for it, and execute and return `operation` result promise. Retries a fixed number of times, if key not provided or valid. */
    async promptForKeyWithRetry(options, operation) {
        let errorCode;
        const vault = await this.getKeyVaultForSigning();
        let signingContext;
        for (let i = 0; i < this.maxRetryCount; i++) {
            try {
                const keyPair = await this.loadOrPrompt(vault, options, errorCode);
                if (!keyPair) {
                    // prompt again for signature key with error code
                    this.logger.logInformation(`Password doesn't open any of the key pairs, no match was found.`);
                    this.logger.logInformation('Reprompting user to enter signature key...');
                    errorCode = 'SignatureKeyError';
                    continue;
                }
                this.logger.logInformation('Key pair found.');
                const signerPublicKeyAsArrayBuffer = await this.cryptoService.exportPublicKey(keyPair.publicKey);
                const signerEntry = vault.findByPublicKey(new Uint8Array(signerPublicKeyAsArrayBuffer));
                if (!signerEntry) {
                    throw new Error(`Signer's entry is not found`);
                }
                signingContext = { keyPair, signerEntry, timeSource: new MonotonicTimeSource() };
                break;
            }
            catch (error) {
                this.logger.logError(`PromptForKey attempt #${i + 1} failed with error:`);
                this.logger.logError(error);
                // modal is closed without entering signature key
                if (error instanceof SignatureKeyNotProvidedError || error instanceof ConfirmModalCancelledError) {
                    throw error;
                }
                errorCode = 'ServiceError';
                continue;
            }
        }
        if (!signingContext) {
            throw new Error(`Couldn't get a valid signature key in ${this.maxRetryCount} retries.`);
        }
        this.logger.logInformation('Running Zero Trust operation...');
        try {
            await operation(signingContext, vault);
        }
        catch (error) {
            this.logger.logError('Zero Trust operation finished with error.');
            this.logger.logError(error);
            throw error;
        }
        this.logger.logInformation('Zero Trust operation succeeded.');
    }
    /** Prompts for signature key only when needed, dictated by ask frequency. */
    async loadOrPrompt(vault, options, errorCode) {
        var _a;
        if (vault.isCompanyKeyUpgradeOverdue() && this.uiFrameAdapter.isAdmin && !((_a = this.uiFrameAdapter.iss) === null || _a === void 0 ? void 0 : _a.endsWith(GotoMyPc))) {
            this.logger.logInformation('Admin user company key is overdue for upgrade, triggering first time flow...');
            this.uiFrameAdapter.triggerFirstTimeFlow();
            throw new SignatureKeyNotProvidedError();
        }
        if (vault.hasEffectiveCompanyKey()) {
            this.logger.logInformation('Company key is effective, attempting to fetch it...');
            const companyKey = await this.companyKeyService.getCompanyKey();
            if (companyKey) {
                this.logger.logInformation('Company key fetched.');
                return await this.withOptionalConfirmation(companyKey, options);
            }
            this.logger.logWarning('Company key could not be fetched.');
        }
        const saved = await this.savedKeyService.loadUsable(vault);
        if (saved.keyPair) {
            return await this.withOptionalConfirmation(saved.keyPair, options);
        }
        // try resolve raw key pair using the signature key entered
        const { keyPair, askFrequency } = await this.keyService.prompt(vault, options.modalText, saved.previousAskFrequency, errorCode);
        if (keyPair && askFrequency !== 'perTask') {
            await this.savedKeyService.save(keyPair, askFrequency);
        }
        return keyPair;
    }
    async withOptionalConfirmation(keyPair, options) {
        if (options.mustConfirmOptions) {
            const confirmed = await this.promptForConfirmation(options.mustConfirmOptions);
            if (!confirmed) {
                throw new ConfirmModalCancelledError();
            }
        }
        return keyPair;
    }
    async promptForConfirmation(modalOptions) {
        this.logger.logInformation('Prompting for confirmation...');
        const result = await this.uiFrameAdapter.triggerConfirmModal(modalOptions);
        this.logger.logInformation(`Confirmation: ${result.confirmed}`);
        return result.confirmed;
    }
    async getKeyVaultForSigning() {
        this.logger.logInformation('User Key Prompt Service: Getting key vault...');
        // Fetch in parallel
        const vaultPromise = this.keyVaultService.getKeyVault();
        const hasPendingRequestPromise = this.vaultRequestRepository.getPendingVaultRequests();
        let hasPendingRequest = false;
        try {
            hasPendingRequest = (await hasPendingRequestPromise)
                .some(p => p.userId === this.uiFrameAdapter.userId);
        }
        catch (ex) {
            this.logger.logWarning(`User Key Prompt Service: ${ex}`);
            this.logger.logInformation(`User Key Prompt Service: Pending requests couldn't be determined. Continuing...`);
        }
        // check whether the user has forgot their key
        if (hasPendingRequest) {
            this.logger.logInformation('User Key Prompt Service: User has pending request.');
            this.uiFrameAdapter.triggerWaitingForApprovalModal();
            throw new Error('User has pending request.');
        }
        // wait for vault
        const vault = await vaultPromise;
        if (vault.isEmpty()) {
            this.logger.logInformation('User Key Prompt Service: Key vault is empty.');
            if (await this.companyKeyService.isKeyCreationEnabled()) {
                this.logger.logInformation('User Key Prompt Service: Initializing company key for "delayed zero trust".');
                const { vault: resultingVault } = await this.companyKeyService.createCompanyKey();
                return resultingVault;
            }
            else {
                this.logger.logInformation('User Key Prompt Service: Company key creation is disabled.');
            }
        }
        // check whether the user has keys to sign with
        const hasUsableKey = vault.hasEffectiveCompanyKey() || vault.hasEffectivePasswordKeyFor(this.uiFrameAdapter.userId);
        if (!hasUsableKey) {
            this.logger.logInformation('User Key Prompt Service: User has no key set yet.');
            this.uiFrameAdapter.triggerFirstTimeFlow();
            throw new Error('Signature key is not set.');
        }
        return vault;
    }
}
